import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';

const CookiePolicy = () => {
  return (
    <>
      <SEO 
        title="Cookie Policy - BlogCraft"
        description="BlogCraft's Cookie Policy - Learn how we use cookies and tracking technologies on our website."
        keywords="cookie policy, cookies, tracking, blogcraft cookies, data privacy"
      />

      <div className="bg-gradient-to-r from-blue-600 to-purple-600 py-12">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl font-bold text-white mb-4">Cookie Policy</h1>
          <p className="text-blue-100">Last updated: January 3, 2026</p>
        </div>
      </div>

      <div className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12">
            <div className="prose prose-lg max-w-none">
              
              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">What Are Cookies?</h2>
                <p className="text-gray-700 mb-4">
                  Cookies are small text files that are stored on your device (computer, tablet, or mobile) when you visit 
                  a website. They are widely used to make websites work more efficiently and to provide information to 
                  website owners.
                </p>
                <p className="text-gray-700">
                  Cookies allow websites to recognize your device and remember information about your preferences or 
                  past actions, which helps improve your browsing experience.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">How We Use Cookies</h2>
                <p className="text-gray-700 mb-4">
                  At BlogCraft, we use cookies for several purposes to enhance your experience and improve our services:
                </p>
                <ul className="list-disc pl-6 text-gray-700">
                  <li><strong>Essential Cookies:</strong> Required for the website to function properly</li>
                  <li><strong>Performance Cookies:</strong> Help us understand how our website is being used</li>
                  <li><strong>Functional Cookies:</strong> Remember your preferences and settings</li>
                  <li><strong>Marketing Cookies:</strong> Used to deliver relevant advertisements and content</li>
                  <li><strong>Analytics Cookies:</strong> Help us analyze website traffic and user behavior</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Types of Cookies We Use</h2>
                
                <h3 className="text-xl font-semibold text-gray-800 mb-3">Essential Cookies</h3>
                <p className="text-gray-700 mb-4">
                  These cookies are necessary for the website to function and cannot be switched off in our systems. 
                  They are usually only set in response to actions made by you which amount to a request for services.
                </p>
                <div className="bg-gray-50 p-4 rounded-lg mb-6">
                  <p className="text-gray-700"><strong>Purpose:</strong> Website functionality, security, and basic operations</p>
                  <p className="text-gray-700"><strong>Duration:</strong> Session or persistent</p>
                  <p className="text-gray-700"><strong>Required:</strong> Yes</p>
                </div>

                <h3 className="text-xl font-semibold text-gray-800 mb-3">Performance and Analytics Cookies</h3>
                <p className="text-gray-700 mb-4">
                  These cookies allow us to count visits and traffic sources so we can measure and improve the performance 
                  of our site. They help us to know which pages are the most and least popular and see how visitors 
                  move around the site.
                </p>
                <div className="bg-gray-50 p-4 rounded-lg mb-6">
                  <p className="text-gray-700"><strong>Purpose:</strong> Website analytics and performance monitoring</p>
                  <p className="text-gray-700"><strong>Services:</strong> Google Analytics, similar analytics tools</p>
                  <p className="text-gray-700"><strong>Duration:</strong> Typically 2 years</p>
                </div>

                <h3 className="text-xl font-semibold text-gray-800 mb-3">Functional Cookies</h3>
                <p className="text-gray-700 mb-4">
                  These cookies enable the website to provide enhanced functionality and personalization. They may be 
                  set by us or by third-party providers whose services we have added to our pages.
                </p>
                <div className="bg-gray-50 p-4 rounded-lg mb-6">
                  <p className="text-gray-700"><strong>Purpose:</strong> Remember user preferences and settings</p>
                  <p className="text-gray-700"><strong>Examples:</strong> Language preferences, theme settings</p>
                  <p className="text-gray-700"><strong>Duration:</strong> Typically 1 year</p>
                </div>

                <h3 className="text-xl font-semibold text-gray-800 mb-3">Marketing and Advertising Cookies</h3>
                <p className="text-gray-700 mb-4">
                  These cookies may be set through our site by our advertising partners to build a profile of your 
                  interests and show you relevant advertisements on other sites.
                </p>
                <div className="bg-gray-50 p-4 rounded-lg mb-6">
                  <p className="text-gray-700"><strong>Purpose:</strong> Personalized advertising and marketing</p>
                  <p className="text-gray-700"><strong>Services:</strong> Google Ads, social media platforms</p>
                  <p className="text-gray-700"><strong>Duration:</strong> Varies by provider</p>
                </div>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Third-Party Cookies</h2>
                <p className="text-gray-700 mb-4">
                  We use various third-party services that may set their own cookies on your device when you visit 
                  our website. These include:
                </p>
                <ul className="list-disc pl-6 text-gray-700">
                  <li><strong>Google Analytics:</strong> For website analytics and user behavior tracking</li>
                  <li><strong>Social Media Platforms:</strong> For sharing buttons and social media integration</li>
                  <li><strong>Email Marketing Services:</strong> For newsletter subscription and delivery</li>
                  <li><strong>Advertising Networks:</strong> For displaying relevant advertisements</li>
                </ul>
                <p className="text-gray-700 mt-4">
                  These third-party services have their own privacy policies and cookie policies. We encourage you to 
                  review these policies to understand how they use cookies.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Managing Your Cookie Preferences</h2>
                <p className="text-gray-700 mb-4">
                  You have several options for managing cookies:
                </p>
                
                <h3 className="text-xl font-semibold text-gray-800 mb-3">Browser Settings</h3>
                <p className="text-gray-700 mb-4">
                  Most web browsers allow you to control cookies through their settings. You can:
                </p>
                <ul className="list-disc pl-6 text-gray-700 mb-4">
                  <li>Accept or reject cookies by default</li>
                  <li>Delete existing cookies from your device</li>
                  <li>Block cookies from specific websites</li>
                  <li>Receive notifications when cookies are set</li>
                </ul>
                <p className="text-gray-700">
                  Please note that blocking certain cookies may affect your ability to use some features of our website.
                </p>

                <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">Cookie Consent Banner</h3>
                <p className="text-gray-700">
                  When you first visit our website, you'll see a cookie consent banner where you can choose which 
                  types of cookies you're comfortable with. You can change your preferences at any time.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Cookie Duration</h2>
                <p className="text-gray-700 mb-4">
                  Cookies have different lifespans depending on their purpose:
                </p>
                <ul className="list-disc pl-6 text-gray-700">
                  <li><strong>Session Cookies:</strong> Expire when you close your browser</li>
                  <li><strong>Persistent Cookies:</strong> Remain on your device for a set period or until deleted</li>
                  <li><strong>First-Party Cookies:</strong> Set by our website directly</li>
                  <li><strong>Third-Party Cookies:</strong> Set by external services we use</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Your Rights</h2>
                <p className="text-gray-700 mb-4">
                  You have the right to:
                </p>
                <ul className="list-disc pl-6 text-gray-700 mb-4">
                  <li>Know what cookies are being used on our website</li>
                  <li>Accept or reject non-essential cookies</li>
                  <li>Withdraw consent at any time</li>
                  <li>Delete cookies from your device</li>
                  <li>Request information about cookies we use</li>
                </ul>
                <p className="text-gray-700">
                  However, please note that some features of our website may not function properly if you reject essential cookies.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Updates to This Policy</h2>
                <p className="text-gray-700">
                  We may update this Cookie Policy from time to time to reflect changes in our practices or for other 
                  operational, legal, or regulatory reasons. We will notify you of any changes by posting the updated 
                  policy on this page and updating the "Last updated" date.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Contact Us</h2>
                <p className="text-gray-700 mb-4">
                  If you have any questions about our use of cookies, please contact us:
                </p>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-gray-700"><strong>Website:</strong> <Link to="/contact" className="text-blue-600 hover:underline">Contact Form</Link></p>
                  <p className="text-gray-700"><strong>Response Time:</strong> We will respond to your inquiry within 7 business days.</p>
                </div>
              </section>

            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CookiePolicy;
